import { BalloonsConfig } from "./Balloons.types";
import {
  animate,
  generatePhysics,
  getRandomInt,
} from "../../functions/helpers";
import { Particle } from "../../Main.types";
import balloon from "./balloon.svg";

const defaultColors = ["#A45BF1", "#25C6F6", "#72F753", "#F76C88", "#F5F770"];
const factors = [-0.6, -0.3, 0, 0.3, 0.6];

const handlePopBalloon = (
  event: MouseEvent,
  element: Element,
  root: Element
) => {
  if (element.parentNode === root) {
    root.removeChild(element);
  }
};

const createElements = (
  root: Element,
  elementCount: number,
  elementSize: number,
  zIndex: number,
  position: string,
  colors: string[]
) =>
  Array.from({ length: elementCount }).map((_, index) => {
    const element = document.createElement("span");
    element.innerHTML = balloon;
    element.style.width = `${elementSize}px`;
    element.style.position = position;
    element.style.color = colors[index % colors.length];
    element.style.zIndex = `${zIndex}`;
    element.addEventListener(
      "click",
      (event: MouseEvent) => {
        handlePopBalloon(event, element, root);
      },
      { once: true }
    );
    root.appendChild(element);
    return { element, differentiator: getRandomInt(0, factors.length) };
  });
const updateParticle = (
  particle: Particle,
  progress: number,
  decay: number,
  lifetime: number
) => {
  const {
    x: x0,
    y: y0,
    tiltAngle: tiltAngle0,
    angle2D,
    velocity: velocity0,
    differentiator,
    wobble: wobble0,
  } = particle.physics;

  // Integrate velocity with exponential decay over progress:
  // velocity(p) = velocity0 * decay^p
  // We multiply by 0.5 in the original code each frame, so integrate that factor as well.
  const velocityDecayTerm =
    decay !== 1 && decay > 0
      ? (decay ** progress - 1) / Math.log(decay)
      : progress; // If decay = 1, integral is simpler.

  // Positions due to velocity:
  // Originally: x += cos(angle2D)*0.5*velocity per frame
  // Integrated: currentX = x0 + ∫(0 to p) cos(angle2D)*0.5*velocity0*(decay^u) du
  const currentX = x0 + 0.5 * Math.cos(angle2D) * velocity0 * velocityDecayTerm;

  // Similarly for y: y += sin(angle2D)*0.5*velocity each frame
  // No other increments per frame for y besides this velocity factor.
  const currentY = y0 + 0.5 * Math.sin(angle2D) * velocity0 * velocityDecayTerm;

  // Tilt angle increments by 0.1 each frame:
  // Assume ~60 frames/second for the given lifetime
  const assumedFrames = 60 * (lifetime / 1000);
  const currentTiltAngle = tiltAngle0 + 0.1 * assumedFrames * progress;

  // Wobble does not increment, remains constant:
  const currentWobble = wobble0;

  // Assuming factors array exists in the global scope or is imported:
  // Use currentX/currentWobble in wobble calculation:
  const wobbleX =
    currentX +
    (factors[differentiator] * progress * currentWobble * currentWobble +
      20 * Math.sin(currentWobble));

  // Determine rotation:
  // If differentiator % 2 is true, use Math.sin(currentTiltAngle/4)/3, else Math.cos(currentTiltAngle/4)/3
  const rotation =
    differentiator % 2
      ? Math.sin(currentTiltAngle / 4) / 3
      : Math.cos(currentTiltAngle / 4) / 3;

  // Scale and opacity:
  const currentScale = 1 - 0.2 * progress;
  let currentOpacity = 1;
  if (progress > 0.5) {
    currentOpacity = 2 - 2 * progress;
  }

  // Update transforms:
  particle.element.style.transform = `translate3d(${wobbleX}px, ${currentY}px, 0) rotate3d(0, 0, 1, ${rotation}rad)`;
  particle.element.style.scale = `${currentScale}`;
  particle.element.style.opacity = `${currentOpacity}`;
};

export const balloons = (
  root: Element,
  internalAnimatingCallback: () => void,
  config?: BalloonsConfig
) => {
  const options = config || {};
  const {
    angle = 90,
    decay = 0.999,
    spread = 50,
    startVelocity = 3,
    elementCount = 10,
    elementSize = 20,
    lifetime = 600,
    zIndex = 0,
    position = "fixed",
    colors = defaultColors,
    onAnimationComplete,
  } = options;
  const spanElements = createElements(
    root,
    elementCount,
    elementSize,
    zIndex,
    position,
    colors
  );
  const particles = spanElements.map(({ element, differentiator }) => ({
    element,
    physics: generatePhysics(angle, spread, startVelocity, differentiator),
  }));

  const onFinish = () => {
    if (typeof onAnimationComplete === "function") {
      onAnimationComplete();
    }
    internalAnimatingCallback();
  };

  animate({ root, particles, decay, lifetime, updateParticle, onFinish });
};
